import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface AssetsCurrentlyAssignedHeaderInfo {
  individualName?: string | null;
  totalCost?: number | null;
  viewPictures?: boolean;
}

export interface AssetsCurrentlyAssignedItem {
  individualName?: string | null;
  phrThr?: string | null;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  size?: string | null;
  recoverableFlag?: string | null;
  cost?: number | null;
  quantityIssued?: number | null;
  quantityAuthorized?: number | null;
  receiptDate?: string | null;
  dueDate?: string | null;
  imageAttached?: boolean;
}

export interface AssetsCurrentlyAssignedGroup {
  individualName: string;
  totalCost?: number | null;
  items: AssetsCurrentlyAssignedItem[];
}

export interface AssetsCurrentlyAssignedTemplateOptions {
  generatedAt: Date;
  header: AssetsCurrentlyAssignedHeaderInfo;
  groups: AssetsCurrentlyAssignedGroup[];
  personLabels?: PersonLabels;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: AssetsCurrentlyAssignedItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="12" class="empty-row">
          No assets are currently assigned for the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item, index) => `
        <tr>
          <td class="index">${index + 1}</td>
          <td>${sanitizeText(item.phrThr)}</td>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.recoverableFlag)}</td>
          <td class="numeric">${formatCurrency(item.cost ?? null)}</td>
          <td class="numeric">${sanitizeText(
            item.quantityIssued !== null && item.quantityIssued !== undefined
              ? String(item.quantityIssued)
              : '',
          )}</td>
          <td class="numeric">${sanitizeText(
            item.quantityAuthorized !== null && item.quantityAuthorized !== undefined
              ? String(item.quantityAuthorized)
              : '',
          )}</td>
          <td>${formatDate(item.receiptDate ?? null)}</td>
          <td>${formatDate(item.dueDate ?? null)}</td>
          <td>${item.imageAttached ? 'Attached (Y)' : '—'}</td>
        </tr>
      `,
    )
    .join('');
};

const buildGroupSections = (
  groups: AssetsCurrentlyAssignedGroup[],
  personLabel = 'Individual',
): string => {
  if (groups.length === 0) {
    return `
      <section class="member-section">
        <table class="member-table">
          <thead>
            <tr>
              <th>#</th>
              <th>PHR/THR</th>
              <th>LIN</th>
              <th>NSN</th>
              <th>Nomenclature</th>
              <th>R/NR</th>
              <th class="numeric">Cost</th>
              <th class="numeric">Qty Iss</th>
              <th class="numeric">Qty Auth</th>
              <th>Receipt Date</th>
              <th>Due Date</th>
              <th>Image</th>
            </tr>
          </thead>
          <tbody>
            ${buildItemsTable([])}
          </tbody>
        </table>
      </section>
    `;
  }

  return groups
    .map((group) => {
      const totalCostLabel = formatCurrency(group.totalCost ?? null);
      const rows = buildItemsTable(group.items);

      return `
        <section class="member-section">
          <div class="member-header">
            <span class="member-label">${personLabel}:</span>
            <span class="member-name">${sanitizeText(group.individualName)}</span>
            <span class="member-label">Total Cost:</span>
            <span class="member-total">${totalCostLabel}</span>
          </div>

          <table class="member-table" data-sheet-name="${sanitizeText(group.individualName)}">
            <thead>
              <tr>
                <th>#</th>
                <th>PHR/THR</th>
                <th>LIN</th>
                <th>NSN</th>
                <th>Nomenclature</th>
                <th>R/NR</th>
                <th class="numeric">Cost</th>
                <th class="numeric">Qty Iss</th>
                <th class="numeric">Qty Auth</th>
                <th>Receipt Date</th>
                <th>Due Date</th>
                <th>Image</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </section>
      `;
    })
    .join('');
};

export const buildAssetsCurrentlyAssignedReportHtml = ({
  generatedAt,
  header,
  groups,
  personLabels,
}: AssetsCurrentlyAssignedTemplateOptions): string => {
  const pl = personLabels ?? {
    label: 'Individual',
    labelPlural: 'Individuals',
    lower: 'individual',
    lowerPlural: 'individuals',
    article: 'an' as const,
  };
  const generatedLabel = formatDateTime(generatedAt);
  const memberName = header.individualName ? sanitizeText(header.individualName) : '—';
  const totalCostLabel = formatCurrency(header.totalCost ?? null);
  const viewPicturesLabel = header.viewPictures ? 'Y' : 'N';
  const groupSections = buildGroupSections(groups, pl.label);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Assets Currently Assigned – ${memberName}</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .meta-block {
        padding: 12px 16px;
        border: 1px solid #e1e8f5;
        border-radius: 8px;
        background: #f8fbff;
      }

      .meta-label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 4px;
      }

      .meta-value {
        font-size: 16px;
        font-weight: 600;
        color: #15263c;
      }

      .member-section {
        margin-bottom: 24px;
        page-break-inside: avoid;
      }

      .member-header {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: baseline;
        padding: 10px 12px;
        background: #e9f1ff;
        border: 1px solid #d7e3f4;
        border-bottom: none;
        border-radius: 6px 6px 0 0;
      }

      .member-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #5c6f85;
      }

      .member-name {
        margin-right: 16px;
        font-size: 14px;
        font-weight: 700;
        color: #15263c;
      }

      .member-total {
        font-size: 14px;
        font-weight: 700;
        color: #15263c;
      }

      .member-table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      .member-table thead th:nth-child(1) { width: 3%; }   /* # */
      .member-table thead th:nth-child(2) { width: 8%; }   /* PHR/THR */
      .member-table thead th:nth-child(3) { width: 9%; }   /* LIN */
      .member-table thead th:nth-child(4) { width: 13%; }  /* NSN */
      .member-table thead th:nth-child(5) { width: 20%; }  /* Nomenclature */
      .member-table thead th:nth-child(6) { width: 5%; }   /* R/NR */
      .member-table thead th:nth-child(7) { width: 8%; }   /* Cost */
      .member-table thead th:nth-child(8) { width: 5%; }   /* Qty Iss */
      .member-table thead th:nth-child(9) { width: 6%; }   /* Qty Auth */
      .member-table thead th:nth-child(10) { width: 8%; }  /* Receipt Date */
      .member-table thead th:nth-child(11) { width: 8%; }  /* Due Date */
      .member-table thead th:nth-child(12) { width: 5%; }  /* Image */

      .member-table thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      .member-table tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      .member-table tbody tr:last-child td {
        border-bottom: none;
      }

      .member-table tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .index {
        text-align: center;
        font-weight: 600;
        color: #4a5c72;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .member-table thead th.numeric {
        text-align: center;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }

        @page {
          size: landscape;
        }

        .member-table {
          table-layout: auto;
        }

        .member-table thead th {
          font-size: 10px;
          padding: 4px 6px;
          letter-spacing: 0;
          white-space: nowrap;
        }

        .member-table tbody td {
          font-size: 10px;
          padding: 4px 6px;
          overflow-wrap: break-word;
        }

        /* Prevent wrapping in short-value columns */
        .member-table tbody td:nth-child(2),
        .member-table tbody td:nth-child(3),
        .member-table tbody td:nth-child(6),
        .member-table tbody td:nth-child(8),
        .member-table tbody td:nth-child(9),
        .member-table tbody td:nth-child(10),
        .member-table tbody td:nth-child(11) {
          white-space: nowrap;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <div class="brand">eQuip Transaction Suite</div>
        <h1 class="title">Assets Currently Assigned</h1>
        <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
      </div>
      <div class="meta-grid">
        <div class="meta-block">
          <span class="meta-label">${pl.label}</span>
          <span class="meta-value">${memberName}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">Total Cost</span>
          <span class="meta-value">${totalCostLabel}</span>
        </div>
        <div class="meta-block">
          <span class="meta-label">View Pictures</span>
          <span class="meta-value">${viewPicturesLabel}</span>
        </div>
      </div>
    </header>

    <section>
      ${groupSections}
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
